import { Collapse } from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import { observer } from "mobx-react";
import * as React from "react";
import { FormattedMessage } from "react-intl";
import { t } from "../../i18n/util";
import { generalStore } from "../../stores/GeneralStore";
import { THEME } from "../../theme/ThemeStore";
import { useMobileMediaQuery } from "../hooks/useMobileMediaQuery";
import { HrBotBackButton, HrBotButton, HrBotDialog, HrBotOutlinedButton, HrBotSwitch } from "./Primitives";
import { AAA_PRIVACY_LINK, DAL_PRIVACY_LINK } from "../../config";

type Props = {
    isOpen: boolean;
    onBack: () => void;
    onSubmit: () => void;
};

export const CookieDialog = observer(({ isOpen, onBack, onSubmit }: Props) => {
    const [open, setOpen] = React.useState(isOpen);
    const [showSettings, setShowSettings] = React.useState(false);

    const [googleAnalytics, setGoogleAnalytics] = React.useState(false);

    const isMobile = useMobileMediaQuery();

    React.useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const handleClose = () => {
        onSubmit();
        setOpen(false);
        if (onBack) {
            onBack();
        }
    };

    const handleSubmit = (googleAnalytics: boolean) => {
        setOpen(false);
        generalStore.googleAnalyticsCookies = googleAnalytics;
        generalStore.acceptedCookies = true;
    };

    return (
        <HrBotDialog open={open}>
            <div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "24px 24px 16px",
                        justifyContent: "space-between",
                        width: "100%"
                    }}
                    className="h3"
                >
                    {t("screen.cookieDialog.header")}
                    <img src={THEME.Images.icClose} alt="" style={{ cursor: "pointer" }} onClick={handleClose} />
                </div>
                <p style={{ padding: "0 24px 24px", borderBottom: `1px solid ${THEME.customColors.borderColor}` }}>
                    <FormattedMessage
                        id="screen.cookieDialog.text"
                        values={{
                            a: (chunks: Array<React.ReactElement | string>) => (
                                <a
                                    href={THEME.THEME_NAME === "aaa" ? AAA_PRIVACY_LINK : DAL_PRIVACY_LINK}
                                    style={{ color: THEME.customColors.primary, textDecoration: "none" }}
                                >
                                    {chunks}
                                </a>
                            )
                        }}
                    />
                </p>
                <div style={{ display: "flex", padding: "16px 0", flexDirection: "column", gap: 32 }}>
                    <CookieSlider label={t("screen.cookieBanner.functionalCookies")} required checked={true} />
                    <CookieSlider
                        label={t("screen.cookieBanner.settings")}
                        dropdown={1}
                        showAll={showSettings}
                        setShowAll={setShowSettings}
                        checked={googleAnalytics}
                        setChecked={setGoogleAnalytics}
                    />
                    <Collapse in={showSettings}>
                        <CookieSlider
                            order={2}
                            label={t("screen.cookieDialog.googleAnalytics")}
                            description={t("screen.cookieDialog.googleAnalytics.description")}
                            checked={googleAnalytics}
                            setChecked={setGoogleAnalytics}
                        />
                    </Collapse>
                </div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        padding: 24,
                        justifyContent: "space-between",
                        flexDirection: isMobile ? "column" : "row",
                        borderTop: `1px solid ${THEME.customColors.borderColor}`
                    }}
                >
                    <HrBotBackButton
                        label={t("screen.cookieBanner.functionalCookies")}
                        onClick={() => handleSubmit(false)}
                    />
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 16,
                            flexDirection: isMobile ? "column" : "row"
                        }}
                    >
                        {THEME.THEME_NAME === "dal" ? (
                            <HrBotButton
                                label={t("screen.applicantDetails.save")}
                                onClick={() => handleSubmit(googleAnalytics)}
                            />
                        ) : (
                            <HrBotOutlinedButton
                                label={t("screen.applicantDetails.save")}
                                onClick={() => handleSubmit(googleAnalytics)}
                            />
                        )}
                        <HrBotButton
                            label={t("screen.cookieBanner.acceptAll")}
                            style={{ padding: "18px 20px" }}
                            onClick={() => handleSubmit(true)}
                        />
                    </div>
                </div>
            </div>
        </HrBotDialog>
    );
});

type CookieSliderProps = {
    order?: number;
    label: string;
    required?: boolean;
    dropdown?: number;
    description?: string;
    showAll?: boolean;
    setShowAll?: React.Dispatch<React.SetStateAction<boolean>>;
    checked?: boolean;
    setChecked?: React.Dispatch<React.SetStateAction<boolean>>;
};

const CookieSlider = observer(
    ({
        order = 1,
        label,
        required,
        dropdown,
        description,
        showAll,
        setShowAll,
        checked,
        setChecked
    }: CookieSliderProps) => {
        return (
            <div style={{ display: "flex", marginLeft: order * 24, gap: 8 }}>
                <HrBotSwitch
                    checked={checked}
                    style={!setChecked ? { cursor: "default" } : {}}
                    onChange={() => {
                        if (setChecked) {
                            setChecked(!checked);
                        }
                    }}
                />
                <div>
                    <div
                        style={{ display: "flex", gap: 4, alignItems: "center", color: THEME.customColors.body1Dark }}
                        className="body2"
                    >
                        <p style={{ color: THEME.customColors.headingsDark }}>{label}</p>
                        {required && t("screen.cookieDialog.required")}
                    </div>
                    {dropdown && (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: "pointer",
                                color: THEME.customColors.primary
                            }}
                            onClick={() => {
                                if (setShowAll) {
                                    setShowAll(!showAll);
                                }
                            }}
                        >
                            {showAll ? (
                                <ArrowUpwardIcon style={{ color: THEME.customColors.primary, width: 14, height: 14 }} />
                            ) : (
                                <ArrowDownwardIcon
                                    style={{ color: THEME.customColors.primary, width: 14, height: 14 }}
                                />
                            )}
                            <div>
                                {dropdown}
                                {t("screen.cookieDialog.service")}
                            </div>
                        </div>
                    )}
                    {description && (
                        <div style={{ color: THEME.customColors.body1Dark }} className="body2">
                            {description}
                        </div>
                    )}
                </div>
            </div>
        );
    }
);
